/**
 * useAgent — React hook for the Chat Agent state machine
 *
 * Manages: config loading, run lifecycle, interleaved transcript,
 * message queue (always-active input), model switching, cost tracking.
 *
 * Key UX: input is NEVER disabled. Users can type and queue messages
 * while generation is running. Queued messages auto-fire as the next
 * round-trip when the current response completes.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { Workspace } from '../engine/types'
import {
    parseAgentConfig,
    type AgentConversationHistory,
    type AgentConfig,
    type AgentRun,
    type AgentRunStatus,
    type TranscriptEvent,
    type CostInfo,
    type ResolvedRef,
    type Attachment,
} from '../engine/agentTypes'
import { createAgentRunController, resolveRefs, saveAttachmentsToRunFolder } from '../engine/agentRuntime'
import { buildConversationHistoryFromActivity } from '../engine/agent/conversationHistory'
import { ensureAgentSessionDirectories } from '../engine/agent/sessionPaths'
import { saveGeneratedImage, promoteGeneratedImage } from '../engine/agent/attachments'
import type { PendingAttachment } from '../components/agent/FileRefInput'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface QueuedMessage {
    id: string
    text: string
    attachments?: PendingAttachment[]
    queuedAt: number
}

/** Options for re-executing a run without duplicating the user message */
interface RetryOptions {
    skipUserEvent: true
    existingRefs?: ResolvedRef[]
    existingAttachments?: Attachment[]
}

export type SpendWarning = 'none' | 'approaching' | 'exceeded'

export interface UseAgentResult {
    // Config
    config: AgentConfig | null
    configError: string | null

    // Runs
    activeRun: AgentRun | null
    status: AgentRunStatus

    // Interleaved transcript (the single stream)
    transcript: TranscriptEvent[]

    // Streaming state
    streamingText: string
    isThinking: boolean

    // Cost
    currentCost: CostInfo
    spendWarning: SpendWarning

    // Queue — always active, user can type during generation
    queue: QueuedMessage[]

    // Actions
    sendMessage: (text: string, attachments?: PendingAttachment[]) => void
    stopRun: () => void
    removeFromQueue: (id: string) => void
    prioritizeQueueItem: (id: string) => void
    retryLastMessage: () => void
    newRun: () => void

    // Artifact promotion
    promoteImage: (event: TranscriptEvent) => Promise<void>
    isImagePromotionPending: (event: TranscriptEvent) => boolean

    // Config mutation
    changeModel: (modelId: string) => void
    updateApiKey: (key: string) => void
    updateConfig: (patch: Partial<AgentConfig>) => void
    toggleProxy: () => void
}

export interface TranscriptFile {
    schema: 'chat-transcript/v2'
    agentPath: string
    sessionId: string
    status?: AgentRunStatus
    config: AgentConfig
    uiEvents: TranscriptEvent[]
    activity: TranscriptEvent[]
    conversation: AgentConversationHistory
    cost: CostInfo
    updatedAt: number
}

interface TranscriptPersistencePayload {
    schema: TranscriptFile['schema']
    agentPath: string
    sessionId: string
    status: AgentRunStatus
    config: AgentConfig
    uiEvents: TranscriptEvent[]
    activity: TranscriptEvent[]
    conversation: AgentConversationHistory
    cost: CostInfo
}

// ────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────

const nextRunId = 1

function getTranscriptPersistenceSignature(payload: TranscriptPersistencePayload): string {
    return JSON.stringify(payload)
}

function getTranscriptSessionId(file: TranscriptFile | (TranscriptFile & { runId?: string })): string {
    const legacyFile = file as TranscriptFile & { runId?: string }
    return file.sessionId || legacyFile.runId || 'unknown'
}

function isAgentRunStatus(value: unknown): value is AgentRunStatus {
    return value === 'idle'
        || value === 'running'
        || value === 'stopping'
        || value === 'completed'
        || value === 'error'
        || value === 'cancelled'
}

function inferHydratedTranscriptStatus(
    events: TranscriptEvent[],
    persistedStatus: unknown,
): AgentRunStatus {
    if (isAgentRunStatus(persistedStatus)) {
        return persistedStatus
    }

    const lastMeaningfulEvent = [...events]
        .reverse()
        .find((event) => event.type !== 'cost_update' && event.type !== 'assistant_stream_delta')

    if (!lastMeaningfulEvent) {
        return 'idle'
    }

    if (lastMeaningfulEvent.type === 'error') {
        return 'error'
    }

    if (
        lastMeaningfulEvent.type === 'assistant_message_final'
        && /\*\(cancelled\)\*\s*$/i.test(lastMeaningfulEvent.text.trim())
    ) {
        return 'cancelled'
    }

    return 'completed'
}

function mergeThinkingText(previousText?: string, nextText?: string): string | undefined {
    if (!previousText) return nextText
    if (!nextText) return previousText
    if (nextText.startsWith(previousText)) return nextText
    if (previousText.endsWith(nextText)) return previousText
    return `${previousText}${nextText}`
}

function getPersistedGeneratedImageCounter(events: TranscriptEvent[]): number {
    let maxCounter = 0
    for (const event of events) {
        if (event.type !== 'assistant_image' || !event.savedPath) continue
        const match = event.savedPath.match(/(?:^|\/)gen-(\d+)-\d+\.[^/.]+$/)
        if (!match) continue
        const counter = Number.parseInt(match[1] ?? '', 10)
        if (Number.isFinite(counter)) {
            maxCounter = Math.max(maxCounter, counter)
        }
    }
    return maxCounter
}

function mergeAdjacentThinkingEvents(events: TranscriptEvent[]): TranscriptEvent[] {
    const merged: TranscriptEvent[] = []

    for (const event of events) {
        const last = merged[merged.length - 1]
        if (event.type === 'assistant_thinking' && last?.type === 'assistant_thinking') {
            merged[merged.length - 1] = {
                ...event,
                text: mergeThinkingText(last.text, event.text),
            }
            continue
        }
        merged.push(event)
    }

    return merged
}

function getSessionIdFromAgentPath(agentPath: string): string {
    const normalizedPath = agentPath.replace(/\/$/, '')
    if (normalizedPath.endsWith('/transcript.json')) {
        const parts = normalizedPath.split('/')
        return parts[parts.length - 2] || 'unknown'
    }
    return normalizedPath.split('/').pop() || 'unknown'
}

function getTranscriptPathForRun(agentPath: string): string {
    return agentPath.endsWith('transcript.json')
        ? agentPath
        : agentPath.endsWith('/')
            ? `${agentPath}transcript.json`
            : `${agentPath}/transcript.json`
}

function shouldPersistTranscriptCheckpoint(
    transcript: TranscriptEvent[],
    status: AgentRunStatus,
    streamingText: string,
    isThinking: boolean,
): boolean {
    if (transcript.length === 0) return false
    if (streamingText || isThinking) return false
    return status === 'completed' || status === 'cancelled' || status === 'error'
}

export function useAgent(agentPath: string, workspace: Workspace): UseAgentResult {
    const configPath = useMemo(() => {
        const isTranscript = agentPath.includes('.agent.json.transcripts/')
        return isTranscript ? agentPath.replace(/\.agent\.json\.transcripts\/.*$/, '.agent.json') : agentPath
    }, [agentPath])
    const isTranscriptView = agentPath.includes('.agent.json.transcripts/')
    const runTranscriptPath = isTranscriptView ? getTranscriptPathForRun(agentPath) : null

    function clearQueuedDispatchTimer() {
        if (queuedDispatchTimerRef.current != null) {
            clearTimeout(queuedDispatchTimerRef.current)
            queuedDispatchTimerRef.current = null
        }
    }

    // ── Config ─────────────────────────────────────────────────
    const [config, setConfig] = useState<AgentConfig | null>(null)
    const [configError, setConfigError] = useState<string | null>(null)

    // Load & watch config
    useEffect(() => {
        let cancelled = false

        function resetToIdle() {
            clearQueuedDispatchTimer()
            lastPersistedTranscriptSignatureRef.current = null
            activityRef.current = []
            generatedImageCounterRef.current = 0
            continueQueueAfterAbortRef.current = false
            setCurrentRunId(null)
            setTranscript([])
            setStreamingText('')
            setIsThinking(false)
            setQueue([])
            setCurrentCost({ tokensIn: 0, tokensOut: 0, estimatedCostUsd: 0 })
            setStatus('idle')
        }

        async function loadConfigOnly() {
            const text = await workspace.fs.readText(configPath)
            if (cancelled) return
            if (!text) {
                setConfigError('Could not read agent config')
                setConfig(null)
                return
            }
            const parsed = parseAgentConfig(text)
            if (!parsed) {
                setConfigError('Invalid agent config format')
                setConfig(null)
                return
            }
            setConfig(parsed)
            setConfigError(null)
        }

        async function hydrateTranscript(transcriptPath: string | null, fallbackToIdle = false) {
            if (!transcriptPath) {
                if (fallbackToIdle) {
                    resetToIdle()
                }
                return
            }

            const runText = await workspace.fs.readText(transcriptPath)
            if (cancelled) return
            if (!runText) {
                if (fallbackToIdle) {
                    resetToIdle()
                }
                return
            }

            try {
                const runData = JSON.parse(runText) as TranscriptFile
                const uiEvents = mergeAdjacentThinkingEvents(runData.uiEvents ?? [])
                const activity = runData.activity ?? []
                const hydratedStatus = inferHydratedTranscriptStatus(uiEvents, runData.status)
                lastPersistedTranscriptSignatureRef.current = getTranscriptPersistenceSignature({
                    schema: 'chat-transcript/v2',
                    agentPath: runData.agentPath,
                    sessionId: getTranscriptSessionId(runData),
                    status: hydratedStatus,
                    config: runData.config,
                    uiEvents,
                    activity,
                    conversation: runData.conversation,
                    cost: runData.cost,
                })
                activityRef.current = activity
                generatedImageCounterRef.current = Math.max(
                    getPersistedGeneratedImageCounter(uiEvents),
                    getPersistedGeneratedImageCounter(activity),
                )
                setCurrentRunId(getTranscriptSessionId(runData))
                setTranscript(uiEvents)
                setStreamingText('')
                setIsThinking(hydratedStatus === 'running' && uiEvents[uiEvents.length - 1]?.type === 'assistant_thinking')
                setCurrentCost(runData.cost)
                setStatus(hydratedStatus)
            } catch (e) {
                console.error('Failed to parse transcript file', e)
                if (fallbackToIdle) {
                    resetToIdle()
                }
            }
        }

        async function loadInitialState() {
            await loadConfigOnly()
            if (isTranscriptView) {
                await hydrateTranscript(runTranscriptPath, true)
                return
            }
            resetToIdle()
        }

        void loadInitialState()

        const handleFsEvent = (evt: { paths?: string[] }) => {
            const paths = evt.paths ?? []
            if (paths.includes(configPath)) {
                void loadConfigOnly()
            }
            if (isTranscriptView && runTranscriptPath && paths.includes(runTranscriptPath)) {
                void hydrateTranscript(runTranscriptPath, false)
            }
        }

        const unsubChanged = workspace.on('fs:changed', handleFsEvent as (payload: unknown) => void)
        const unsubExternal = workspace.on('fs:external-change', handleFsEvent as (payload: unknown) => void)
        return () => {
            cancelled = true
            clearQueuedDispatchTimer()
            unsubChanged()
            unsubExternal()
        }
    }, [agentPath, configPath, isTranscriptView, runTranscriptPath, workspace])

    // ── Run state ──────────────────────────────────────────────
    const [status, setStatus] = useState<AgentRunStatus>('idle')
    const [transcript, setTranscript] = useState<TranscriptEvent[]>([])
    const [streamingText, setStreamingText] = useState('')
    const [isThinking, setIsThinking] = useState(false)
    const [currentCost, setCurrentCost] = useState<CostInfo>({ tokensIn: 0, tokensOut: 0, estimatedCostUsd: 0 })
    const [queue, setQueue] = useState<QueuedMessage[]>([])
    const [currentRunId, setCurrentRunId] = useState<string | null>(null)

    const controllerRef = useRef<ReturnType<typeof createAgentRunController> | null>(null)
    const runningRef = useRef(false)
    const continueQueueAfterAbortRef = useRef(false)
    const queuedDispatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const executeRunRef = useRef<((userMessage: string, attachments?: PendingAttachment[]) => Promise<void>) | null>(null)
    const lastPersistedTranscriptSignatureRef = useRef<string | null>(null)
    const activityRef = useRef<TranscriptEvent[]>([])
    const generatedImageCounterRef = useRef(0)
    const pendingPromotionsRef = useRef<Map<string, Promise<void>>>(new Map())
    const [pendingPromotionPaths, setPendingPromotionPaths] = useState<ReadonlySet<string>>(() => new Set())
    const ensureSessionId = useCallback((): string => {
        if (currentRunId) return currentRunId
        const nextSessionId = agentPath.includes('.agent.json.transcripts/')
            ? getSessionIdFromAgentPath(agentPath)
            : `session-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
        setCurrentRunId(nextSessionId)
        return nextSessionId
    }, [agentPath, currentRunId])

    // ── Spend warning ──────────────────────────────────────────
    const spendWarning = useMemo<SpendWarning>(() => {
        const limitRaw = config?.limits?.maxSpendUsd ?? config?.limits?.softSpendUsd
        if (!limitRaw) return 'none'
        const limit = parseFloat(limitRaw)
        if (Number.isNaN(limit) || limit <= 0) return 'none'
        const ratio = currentCost.estimatedCostUsd / limit
        if (ratio >= 1) return 'exceeded'
        if (ratio >= 0.9) return 'approaching'
        return 'none'
    }, [config, currentCost])

    // ── Execute a single run ───────────────────────────────────
    const dispatchNextQueuedMessage = useCallback(() => {
        setQueue(prev => {
            if (prev.length === 0) return prev
            const [next, ...rest] = prev
            clearQueuedDispatchTimer()
            queuedDispatchTimerRef.current = setTimeout(() => {
                queuedDispatchTimerRef.current = null
                void executeRunRef.current?.(next.text, next.attachments)
            }, 50)
            return rest
        })
    }, [])

    const executeRun = useCallback(async (userMessage: string, attachments?: PendingAttachment[], retryOpts?: RetryOptions) => {
        if (!config || runningRef.current) return
        runningRef.current = true
        setStatus('running')
        setStreamingText('')
        setIsThinking(false)

        // Resolve @-references (reuse existing refs on retry when available)
        const refs = retryOpts?.existingRefs ?? await resolveRefs(userMessage, workspace, config)

        const history = await buildConversationHistoryFromActivity(config, activityRef.current, workspace)

        const sessionId = ensureSessionId()

        const engineAttachments = retryOpts?.existingAttachments
            ?? (attachments?.length
                ? await saveAttachmentsToRunFolder(
                    attachments.map((attachment) => attachment.file),
                    workspace,
                    configPath,
                    sessionId,
                )
                : undefined)

        // Add user message to transcript (skip on retry — it already exists)
        if (!retryOpts?.skipUserEvent) {
            const userEvent: TranscriptEvent = {
                type: 'user_message',
                text: userMessage,
                refs,
                attachments: engineAttachments,
                timestamp: Date.now(),
            }
            activityRef.current = [...activityRef.current, userEvent]
            setTranscript(prev => [...prev, userEvent])
        }

        // Create controller and start streaming
        const controller = createAgentRunController(config, workspace)
        controllerRef.current = controller
        let accumulated = ''
        let wasAborted = false
        let providerErrored = false
        let hasAssistantMessageFinal = false

        try {
            const iter = controller.run(userMessage, refs, engineAttachments, history)
            for await (const event of iter) {
                const shouldMaterializePartialAssistant = event.type === 'error' && accumulated && !hasAssistantMessageFinal
                if (event.type === 'assistant_message_final') {
                    const resolvedEvent = { ...event, text: accumulated || event.text }
                    activityRef.current = [...activityRef.current, resolvedEvent]
                    hasAssistantMessageFinal = true
                } else if (shouldMaterializePartialAssistant) {
                    const partialEvent: TranscriptEvent = {
                        type: 'assistant_message_final',
                        text: accumulated,
                        timestamp: event.timestamp,
                    }
                    activityRef.current = [...activityRef.current, partialEvent, event]
                    hasAssistantMessageFinal = true
                } else {
                    activityRef.current = [...activityRef.current, event]
                }

                // Append to transcript
                if (event.type === 'tool_call') {
                    // For tool_call status updates, replace the existing tool_call with same id
                    setTranscript(prev => {
                        const existingIdx = prev.findIndex(
                            e => e.type === 'tool_call' && e.id === event.id,
                        )
                        if (existingIdx >= 0) {
                            const next = [...prev]
                            next[existingIdx] = event
                            return next
                        }
                        return [...prev, event]
                    })
                } else if (event.type === 'assistant_stream_delta') {
                    accumulated += event.text
                    setStreamingText(accumulated)
                    // Don't add deltas to transcript — we'll add the final
                } else if (event.type === 'assistant_thinking') {
                    setIsThinking(true)
                    setTranscript(prev => {
                        const last = prev[prev.length - 1]
                        if (last?.type !== 'assistant_thinking') {
                            return [...prev, event]
                        }
                        const previousText = last.text ?? ''
                        const nextText = event.text ?? ''
                        const mergedText = mergeThinkingText(previousText, nextText) ?? ''
                        if (!mergedText.trim() && !event.text) {
                            return prev
                        }
                        const next = [...prev]
                        next[next.length - 1] = {
                            ...last,
                            text: mergedText.trim() ? mergedText : undefined,
                        }
                        return next
                    })
                } else if (event.type === 'assistant_message_final') {
                    setIsThinking(false)
                    setStreamingText('')
                    setTranscript(prev => [...prev, { ...event, text: accumulated || event.text }])
                } else if (event.type === 'cost_update') {
                    setCurrentCost({
                        tokensIn: event.tokensIn,
                        tokensOut: event.tokensOut,
                        estimatedCostUsd: event.estimatedCostUsd,
                    })
                } else if (event.type === 'assistant_image') {
                    // Materialize generated image into session folder
                    const savedPath = await saveGeneratedImage(
                        event.dataUrl,
                        workspace,
                        configPath,
                        sessionId,
                        ++generatedImageCounterRef.current,
                    )
                    const resolvedEvent: TranscriptEvent = savedPath
                        ? { ...event, savedPath }
                        : event
                    setTranscript(prev => [...prev, resolvedEvent])
                    // Update activity log with resolved event
                    const lastIdx = activityRef.current.length - 1
                    if (lastIdx >= 0 && activityRef.current[lastIdx] === event) {
                        activityRef.current[lastIdx] = resolvedEvent
                    }
                } else if (event.type === 'assistant_tool_batch' || event.type === 'assistant_reasoning_item') {
                    continue
                } else {
                    if (event.type === 'error') providerErrored = true
                    if (shouldMaterializePartialAssistant) {
                        const partialEvent: TranscriptEvent = {
                            type: 'assistant_message_final',
                            text: accumulated,
                            timestamp: event.timestamp,
                        }
                        setTranscript(prev => [...prev, partialEvent, event])
                    } else {
                        setTranscript(prev => [...prev, event])
                    }
                }

                // Clear thinking as soon as text streaming starts.
                // Use unconditional setIsThinking(false) instead of reading
                // the stale closure value — React bails out when the value
                // is already false, so this is both correct and cheap.
                if (event.type === 'assistant_stream_delta') {
                    setIsThinking(false)
                }
            }

            setStatus(providerErrored ? 'error' : 'completed')
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                wasAborted = true
                // Preserve partial response
                if (accumulated) {
                    const cancelledEvent: TranscriptEvent = {
                        type: 'assistant_message_final',
                        text: `${accumulated}\n\n*(cancelled)*`,
                        timestamp: Date.now(),
                    }
                    activityRef.current = [...activityRef.current, cancelledEvent]
                    setTranscript(prev => [...prev, cancelledEvent])
                }
                setStatus('cancelled')
            } else {
                const partialEvent = accumulated && !hasAssistantMessageFinal
                    ? {
                        type: 'assistant_message_final' as const,
                        text: accumulated,
                        timestamp: Date.now(),
                    }
                    : null
                const errorEvent: TranscriptEvent = {
                    type: 'error',
                    message: String(err),
                    retryable: true,
                    timestamp: Date.now(),
                }
                if (partialEvent) {
                    hasAssistantMessageFinal = true
                    activityRef.current = [...activityRef.current, partialEvent, errorEvent]
                    setTranscript(prev => [...prev, partialEvent, errorEvent])
                } else {
                    activityRef.current = [...activityRef.current, errorEvent]
                    setTranscript(prev => [...prev, errorEvent])
                }
                setStatus('error')
            }
        } finally {
            setStreamingText('')
            setIsThinking(false)
            runningRef.current = false
            controllerRef.current = null
        }

        const shouldContinueQueue = !wasAborted || continueQueueAfterAbortRef.current
        continueQueueAfterAbortRef.current = false

        if (shouldContinueQueue) {
            dispatchNextQueuedMessage()
        }
    }, [config, workspace, ensureSessionId, configPath, dispatchNextQueuedMessage])

    useEffect(() => {
        executeRunRef.current = executeRun
    }, [executeRun])

    // ── Persistence ───────────────────────────────────────────
    // Auto-save transcript
    useEffect(() => {
        if (!config || transcript.length === 0) return

        const shouldCreateInitialTranscript = lastPersistedTranscriptSignatureRef.current == null
        const shouldWriteCheckpoint = shouldPersistTranscriptCheckpoint(transcript, status, streamingText, isThinking)
        if (!shouldCreateInitialTranscript && !shouldWriteCheckpoint) return

        let runId = currentRunId
        if (!runId) {
            const isTranscriptFolder = agentPath.includes('.agent.json.transcripts/')
            if (isTranscriptFolder) {
                // Extract session ID from folder path: .../session-xxx/
                runId = getSessionIdFromAgentPath(agentPath)
                setCurrentRunId(runId)
            } else {
                // Generate a new session ID for fresh chats
                runId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
                setCurrentRunId(runId)
            }
        }

        const timer = setTimeout(() => {
            const activitySnapshot = [...activityRef.current]
            void (async () => {
                const conversation = await buildConversationHistoryFromActivity(config, activitySnapshot, workspace)
                const persistencePayload: TranscriptPersistencePayload = {
                    schema: 'chat-transcript/v2',
                    agentPath: configPath,
                    sessionId: runId!,
                    status,
                    config,
                    uiEvents: transcript,
                    activity: activitySnapshot,
                    conversation,
                    cost: currentCost,
                }
                const persistenceSignature = getTranscriptPersistenceSignature(persistencePayload)
                if (lastPersistedTranscriptSignatureRef.current === persistenceSignature) return

                const runPaths = ensureAgentSessionDirectories(workspace, agentPath, runId!)
                const fileData: TranscriptFile = {
                    ...persistencePayload,
                    updatedAt: Date.now(),
                }
                workspace.fs.writeText(runPaths.transcriptPath, JSON.stringify(fileData, null, 2))
                lastPersistedTranscriptSignatureRef.current = persistenceSignature
            })()
        }, shouldCreateInitialTranscript ? 0 : 500)
        return () => clearTimeout(timer)
    }, [transcript, config, agentPath, workspace, configPath, currentCost, currentRunId, status, streamingText, isThinking])

    // ── Public actions ─────────────────────────────────────────

    const sendMessage = useCallback((text: string, attachments?: PendingAttachment[]) => {
        const trimmed = text.trim()
        if (!trimmed && (!attachments || attachments.length === 0)) return

        if (runningRef.current) {
            // Queue the message — always-active input, never block the user
            setQueue(prev => [...prev, {
                id: Math.random().toString(36).slice(2, 8),
                text: trimmed,
                attachments,
                queuedAt: Date.now(),
            }])
        } else {
            executeRun(trimmed, attachments)
        }
    }, [executeRun])

    const stopRun = useCallback(() => {
        if (controllerRef.current) {
            continueQueueAfterAbortRef.current = false
            setStatus('stopping')
            controllerRef.current.abort()
        }
    }, [])

    const removeFromQueue = useCallback((id: string) => {
        setQueue(prev => prev.filter(m => m.id !== id))
    }, [])

    const prioritizeQueueItem = useCallback((id: string) => {
        const selectedIndex = queue.findIndex(message => message.id === id)
        if (selectedIndex < 0) return

        const messageToRun = queue[selectedIndex]

        if (selectedIndex > 0) {
            const nextQueue = [...queue]
            nextQueue[selectedIndex] = nextQueue[selectedIndex - 1]
            nextQueue[selectedIndex - 1] = messageToRun
            setQueue(nextQueue)
            return
        }

        if (runningRef.current || status === 'stopping') {
            continueQueueAfterAbortRef.current = true
            if (controllerRef.current && status !== 'stopping') {
                setStatus('stopping')
                controllerRef.current.abort()
            }
            return
        }

        setQueue(queue.slice(1))
        executeRun(messageToRun.text, messageToRun.attachments)
    }, [executeRun, queue, status])

    const retryLastMessage = useCallback(() => {
        const lastUser = [...transcript].reverse().find(e => e.type === 'user_message')
        if (!lastUser || lastUser.type !== 'user_message') return

        // Rewind only the current failed turn so retry keeps older transcript
        // history intact, including earlier error events from previous turns.
        const lastUserIdx = transcript.lastIndexOf(lastUser)
        const cleaned = transcript.slice(0, lastUserIdx + 1)
        activityRef.current = cleaned
        setTranscript(cleaned)

        const retryOpts: RetryOptions = {
            skipUserEvent: true,
            existingRefs: lastUser.refs,
            existingAttachments: lastUser.attachments,
        }
        executeRun(lastUser.text, undefined, retryOpts)
    }, [transcript, executeRun])

    const setImagePromotionPending = useCallback((savedPath: string, pending: boolean) => {
        setPendingPromotionPaths((prev) => {
            const alreadyPending = prev.has(savedPath)
            if (alreadyPending === pending) return prev

            const next = new Set(prev)
            if (pending) {
                next.add(savedPath)
            } else {
                next.delete(savedPath)
            }
            return next
        })
    }, [])

    const newRun = useCallback(() => {
        clearQueuedDispatchTimer()
        lastPersistedTranscriptSignatureRef.current = null
        activityRef.current = []
        generatedImageCounterRef.current = 0
        continueQueueAfterAbortRef.current = false
        setTranscript([])
        setStreamingText('')
        setIsThinking(false)
        setQueue([])
        setCurrentCost({ tokensIn: 0, tokensOut: 0, estimatedCostUsd: 0 })
        setStatus('idle')
        setCurrentRunId(null)
    }, [])

    // ── Artifact promotion ────────────────────────────────────────

    const promoteImage = useCallback((event: TranscriptEvent): Promise<void> => {
        if (event.type !== 'assistant_image' || !event.savedPath || event.promotedPath) return Promise.resolve()

        const savedPath = event.savedPath
        const syncPromotedPath = (promotedPath: string) => {
            setTranscript(prev => {
                let changed = false
                const next = prev.map((entry) => {
                    if (entry.type !== 'assistant_image' || entry.savedPath !== savedPath || entry.promotedPath === promotedPath) {
                        return entry
                    }
                    changed = true
                    return { ...entry, promotedPath }
                })
                return changed ? next : prev
            })
            activityRef.current = activityRef.current.map((entry) => {
                if (entry.type !== 'assistant_image' || entry.savedPath !== savedPath || entry.promotedPath === promotedPath) {
                    return entry
                }
                return { ...entry, promotedPath }
            })
        }

        const existingPromotedPath = activityRef.current.find((
            entry,
        ): entry is Extract<TranscriptEvent, { type: 'assistant_image' }> =>
            entry.type === 'assistant_image' && entry.savedPath === savedPath && Boolean(entry.promotedPath),
        )?.promotedPath
        if (existingPromotedPath) {
            syncPromotedPath(existingPromotedPath)
            return Promise.resolve()
        }

        const existingPromotion = pendingPromotionsRef.current.get(savedPath)
        if (existingPromotion) return existingPromotion

        const promotionPromise = (async () => {
            const promotedPath = await promoteGeneratedImage(
                savedPath,
                workspace,
                configPath,
            )
            if (!promotedPath) return

            syncPromotedPath(promotedPath)
        })().finally(() => {
            if (pendingPromotionsRef.current.get(savedPath) === promotionPromise) {
                pendingPromotionsRef.current.delete(savedPath)
                setImagePromotionPending(savedPath, false)
            }
        })

        pendingPromotionsRef.current.set(savedPath, promotionPromise)
        setImagePromotionPending(savedPath, true)
        return promotionPromise
    }, [workspace, configPath, setImagePromotionPending])

    const isImagePromotionPending = useCallback((event: TranscriptEvent): boolean => {
        return event.type === 'assistant_image' && Boolean(event.savedPath) && pendingPromotionPaths.has(event.savedPath)
    }, [pendingPromotionPaths])

    // ── Config mutation ────────────────────────────────────────

    const persistConfig = useCallback((updated: AgentConfig) => {
        setConfig(updated)
        workspace.fs.writeText(configPath, JSON.stringify(updated, null, 2))
    }, [configPath, workspace])

    const changeModel = useCallback((modelId: string) => {
        if (!config) return
        persistConfig({ ...config, model: modelId })
    }, [config, persistConfig])

    const updateApiKey = useCallback((key: string) => {
        if (!config) return
        persistConfig({
            ...config,
            provider: { ...config.provider, apiKey: key },
        })
    }, [config, persistConfig])

    const updateConfig = useCallback((patch: Partial<AgentConfig>) => {
        if (!config) return
        persistConfig({ ...config, ...patch })
    }, [config, persistConfig])

    const toggleProxy = useCallback(() => {
        if (!config) return
        persistConfig({
            ...config,
            provider: {
                ...config.provider,
                useProxy: config.provider.useProxy !== true,
            },
        })
    }, [config, persistConfig])

    // ── Active run (for cost badge etc.) ───────────────────────
    const activeRun = useMemo<AgentRun | null>(() => {
        if (status === 'idle' && transcript.length === 0) return null
        return {
            runId: `run-${nextRunId}`,
            status,
            transcript,
            cost: currentCost,
            createdAt: transcript[0]?.timestamp ?? Date.now(),
            completedAt: status === 'completed' || status === 'cancelled' || status === 'error'
                ? Date.now()
                : undefined,
        }
    }, [status, transcript, currentCost])

    return {
        config,
        configError,
        activeRun,
        status,
        transcript,
        streamingText,
        isThinking,
        currentCost,
        spendWarning,
        queue,
        sendMessage,
        stopRun,
        removeFromQueue,
        prioritizeQueueItem,
        retryLastMessage,
        newRun,
        promoteImage,
        isImagePromotionPending,
        changeModel,
        updateApiKey,
        updateConfig,
        toggleProxy,
    }
}
